import { forwardRef, KeyboardEvent, memo, MouseEvent, ReactNode, useCallback } from "react";
import classNames from "classnames";
import BaseModal, { Classes, Props } from "react-modal";
import { useMergedRef } from "../../utils";
import { Button, ButtonProps } from "../Button";
import { CloseButton } from "../CloseButton";
import { Overlay } from "../Overlay";
import { ProgressButton, ProgressButtonProps } from "../ProgressButton";
import { Scrollbars } from "../Scrollbars";

import styles from "./styles.module.scss";

const contentRef = (ref: HTMLDivElement) => {
  // fix modal height
  if (ref) {
    ref.style.height = "0";
    ref.style.overflow = "hidden";
    ref.style.padding = "0";
    setTimeout(() => {
      ref.style.height = "";
      ref.style.overflow = "";
      ref.style.padding = "";
    }, 0);
  }
};

export const setModalAppElement = BaseModal.setAppElement;

export type ModalProps = Props & {
  header?: ReactNode | ReactNode[]
  actions?: ReactNode | ReactNode[]
  divider?: boolean
  onSubmit?: () => boolean | Promise<boolean>
  scrollable?: boolean
  isSubmitting?: boolean
  showCloseButton?: boolean
  headerClassName?: string
  actionsClassName?: string
  contentClassName?: string
  buttonsClassName?: string
  submitButtonProps?: ProgressButtonProps | false
  cancelButtonProps?: ButtonProps | false
  renderDefaultButtons?: "horizontal" | "vertical" | "none" | false
}

export const Modal = memo(forwardRef<BaseModal, ModalProps>((
  {
    header,
    divider,
    actions = null,
    children,
    onSubmit = () => true,
    className,
    scrollable = true,
    isSubmitting,
    onRequestClose,
    showCloseButton = true,
    headerClassName,
    overlayClassName,
    buttonsClassName,
    actionsClassName,
    contentClassName,
    submitButtonProps,
    cancelButtonProps,
    //todo fix
    renderDefaultButtons = true,
    ...props
  }, ref) => {
  const onSubmitClick = useCallback(async (e: MouseEvent | KeyboardEvent) => {
    if (await onSubmit?.()) {
      onRequestClose?.(e);
    }
  }, [ onSubmit, onRequestClose ]);

  return (
    <BaseModal
      className={(!className || typeof className === "string")
        ? classNames(styles.root, className)
        : {
          ...className,
          base: classNames(styles.root, className?.base),
        } as Classes
      }
      overlayElement={(overlayProps, contentEl) => (
        <Overlay backgroundType="blurred" {...overlayProps}>
          {contentEl}
        </Overlay>
      )}
      contentRef={useMergedRef(contentRef, props.contentRef as any)}
      onRequestClose={onRequestClose}
      ref={ref}
      {...props}
    >
      {header &&
        <div key="header" className={classNames(styles.header, headerClassName)}>
          {header}
          {showCloseButton && <CloseButton key="close" className={styles.closeButton} onClick={onRequestClose} />}
        </div>
      }
      {children && (
        scrollable
          ? (
            <Scrollbars
              key="scrollbars"
              className={classNames(styles.scrollbar, contentClassName, "os-host-flexbox")}>
              {children}
            </Scrollbars>
          )
          : (
            <div key="children" className={classNames(styles.scrollbar, contentClassName)}>
              {children}
            </div>
          ))
      }
      {
        (actions || renderDefaultButtons) &&
        <div key="actions" className={classNames(styles.actions, actionsClassName, {
          [styles.divider]: divider,
        })}>
          {actions}
          {renderDefaultButtons &&
            <div key={"button"} className={classNames(
              styles.buttons,
              buttonsClassName,
              styles[renderDefaultButtons as keyof typeof styles],
            )}>
              {cancelButtonProps &&
                <Button
                  key="cancelButton"
                  buttonType="secondaryGray"
                  buttonSize="lg"
                  children="Cancel"
                  onClick={onRequestClose}
                  {...cancelButtonProps}
                  className={classNames(styles.cancelButton, cancelButtonProps?.className)}
                />
              }
              {submitButtonProps &&
                <ProgressButton
                  key="submitButton"
                  buttonType="primary"
                  buttonSize="lg"
                  children="Submit"
                  processing={isSubmitting}
                  onClick={onSubmitClick}
                  {...submitButtonProps}
                  className={classNames(styles.submitButton, submitButtonProps?.className)}
                />
              }
            </div>
          }
        </div>
      }
    </BaseModal>
  );
}));